import 'velocity-animate';

import { clearRetailTokenFromPersistentStorage, killToken } from '@retail/core';
import { getFeatureConfig } from '@luna/feature-config';
import type { LogoutType } from '@luna/utils-ts';
import { isOpenIdConnectEnabled } from '@shared/environment';
import type { LogoutConfig, LogoutRequestInfo } from '@shared/auth';
import { getToken, logout } from '@shared/auth';
import { deleteTrackingSessionData } from '@shared/statistics';

import { renderLogoutMessage } from './renderLogoutMessage';
import { getLogoutUrl } from '../LogoutUrlService';
import { logoutWithOIDC } from '../oidc/OidcLogoutService';

function createLogoutCallback(logoutUrl: string): () => void {
    return () => {
        window.location.href = logoutUrl;
    };
}

export async function setLogoutCallbacks(logoutUrl: string, logoutIssueUrl: string): Promise<void> {
    const logoutIssueCb = createLogoutCallback(logoutIssueUrl);
    setTimeout(
        () => {
            clearRetailTokenFromPersistentStorage();
            logoutIssueCb();
        },
        getFeatureConfig((config) => config.base.logoutIssueTimeout),
    );

    const cb = createLogoutCallback(logoutUrl);

    await killToken().then(cb).catch(logoutIssueCb);
}

export const retailLogoutConfiguration: LogoutConfig = {
    logoutCallback: retailLogout,
    clearToken: clearRetailTokenFromPersistentStorage,
};

async function retailLogout(
    logoutType: LogoutType = 'unknown',
    requestInfo?: LogoutRequestInfo,
): Promise<void> {
    renderLogoutMessage(logoutType);

    const currentLogoutType: LogoutType = logoutType || 'tokenkill';
    const logoutUrl = getLogoutUrl(currentLogoutType, requestInfo?.url ?? '');
    const logoutIssueUrl = getLogoutUrl(
        getFeatureConfig((config) => config.base.logoutIssueLogoutType),
    );

    deleteTrackingSessionData();

    if (isOpenIdConnectEnabled()) {
        await logoutWithOIDC();
        return;
    }
    await setLogoutCallbacks(logoutUrl, logoutIssueUrl);
}

/**
 * When page is loaded from cache, it might be in browser history
 * but actual token is already destroyed (user has been logged out),
 * but cached data are in browser, we must ensure no visibility of this data.
 */
export function preventLoadingFromCache(): void {
    window.onpageshow = function onpageshow() {
        if (getToken() === '') {
            logout();
        }
    };
}
